import React from 'react';

import { DiscountType } from '../../../Api/CRM/Responses/LotteryTicketResponse';
import LotteryTicket from '../../../Api/Models/LotteryTicket';
import {
    formatCurrency,
    formatDateStringWithTimezone,
    formatPercentage,
    formatTicketAmountWithDiscount,
    getCrmTicketPrizeGrossFormatted,
    getCrmTicketPrizeNetFormatted,
    getCrmTicketPrizeTaxFixedFormatted,
    getCrmTicketPrizeTaxSumFormatted,
} from '../../../utils/formatters';
import ProtectedLink from '../../elements/ProtectedLink';
import BooleanIcon from '../../elements/booleanIcon';
import Card from '../../elements/common/Card';
import ColoredStatus from '../../elements/common/ColoredStatus';
import Row, { TableDetailsBody } from '../../elements/common/TableDetailsRow';
import renderNumbers from '../../elements/lotteries/Numbers';

import { PERMISSION } from 'crm/helpers/Permissions';

const currencyCode = process.env.MIX_CURRENCY;

function TicketDetails({ ticket }: Readonly<{ ticket: LotteryTicket }>) {
    function getBonusDisplay() {
        switch (ticket.discount_type) {
            case DiscountType.TICKET:
                return 'Ticket';
            case DiscountType.PERCENTAGE_DISCOUNT:
                return 'Discount (%)';
            case DiscountType.AMOUNT_DISCOUNT:
                return `Discount (${currencyCode})`;
            default:
                return null;
        }
    }

    function getDiscount() {
        switch (ticket.discount_type) {
            case DiscountType.TICKET:
                return '100%';
            case DiscountType.PERCENTAGE_DISCOUNT:
                return `${ticket.system_discount_amount}%`;
            case DiscountType.AMOUNT_DISCOUNT:
                return formatCurrency(ticket.system_discount_amount);
            default:
                return null;
        }
    }

    return (
        <Card title="Ticket details">
            <TableDetailsBody>
                <Row label="Token">{ticket.token}</Row>
                <Row label="Official UUID">{ticket.official_uuid}</Row>
                <Row label="Email">
                    <ProtectedLink
                        url={`/users/${ticket.user_id}`}
                        value={ticket.email}
                        permissions={[PERMISSION.VIEW_USER]}
                    />
                </Row>
                <Row label="Name">{ticket.name}</Row>
                <Row label="Surname">{ticket.surname}</Row>
                <Row label="Phone">{ticket.phone}</Row>
                <Row label="IP address">{ticket.ip}</Row>
                <Row label="Bought by">
                    {ticket.reseller_customer_id ? (
                        <ProtectedLink
                            url={`/resellers/${ticket.reseller_customer_id}`}
                            value={ticket.reseller_customer_email}
                            permissions={[PERMISSION.VIEW_RESELLER]}
                        />
                    ) : (
                        'Customer'
                    )}
                </Row>
                <Row label="Lottery name">
                    <ProtectedLink
                        url={`/lotteries/${ticket.lottery_slug}`}
                        value={ticket.lottery_name}
                        permissions={[PERMISSION.VIEW_LOTTERY]}
                    />
                </Row>
                <Row label="Status">
                    <ColoredStatus status={ticket.status} />
                </Row>
                <Row label="Paid out">
                    <BooleanIcon value={ticket.paid_out} />
                </Row>
                <Row label="Prize net">{getCrmTicketPrizeNetFormatted(ticket)}</Row>
                <Row label="Prize gross">{getCrmTicketPrizeGrossFormatted(ticket)}</Row>
                <Row label="Prize tax percent">{formatPercentage(ticket.prize_tax_percent)}</Row>
                <Row label="Prize tax fixed">{getCrmTicketPrizeTaxFixedFormatted(ticket)}</Row>
                <Row label="Prize tax sum">{getCrmTicketPrizeTaxSumFormatted(ticket)}</Row>
                <Row label="Amount net">
                    {formatTicketAmountWithDiscount(
                        ticket.system_sell_amount_net,
                        ticket.system_sell_amount_currency_code,
                        ticket.discount_type,
                        ticket.system_discount_amount,
                        ticket.system_ticket_value
                    )}
                </Row>
                <Row label="Amount gross">
                    {formatTicketAmountWithDiscount(
                        ticket.system_sell_amount_gross,
                        ticket.system_sell_amount_currency_code,
                        ticket.discount_type,
                        ticket.system_discount_amount,
                        ticket.system_ticket_value
                    )}
                </Row>
                <Row label="Sale tax percent">{formatPercentage(ticket.sale_tax_percent)}</Row>
                <Row label="Sale tax fixed">{formatCurrency(ticket.system_sale_tax_fixed)}</Row>
                <Row label="Sale tax sum">{formatCurrency(ticket.system_sale_tax_sum)}</Row>
                <Row label="Print count">{ticket.print_count}</Row>
                <Row label="Is printed?">
                    {ticket.print_count > 0 ? 'Ticket was printed' : 'Ticket not printed yet'}
                </Row>
                <Row label="Draw date">{formatDateStringWithTimezone(ticket.draw_date)}</Row>
                <Row label="Created at">{formatDateStringWithTimezone(ticket.created_at)}</Row>
                {ticket.draw_numbers && (
                    <Row label="Draw numbers">
                        {renderNumbers(ticket.draw_numbers, ticket.lottery_id, ticket.lottery_type)}
                    </Row>
                )}
                <Row label="Multiplier">{ticket.multiplier}</Row>
                {ticket.promo_code_campaign && (
                    <Row label="Promo Code Campaign">
                        Bonus type: {getBonusDisplay()}
                        <br />
                        Discount: {getDiscount()}
                        <br />
                        {!!ticket.promo_code_id && `Promo Code: ${ticket.promo_code.value}`} <br />
                        <ProtectedLink
                            url={`/promo_codes/${ticket.promo_code_campaign_id}`}
                            value="View Promo Code"
                            permissions={[PERMISSION.VIEW_PROMO_CODE]}
                        />
                    </Row>
                )}
                {!!ticket.game_type && <Row label="Game type">{ticket.game_type}</Row>}
            </TableDetailsBody>
        </Card>
    );
}

export default TicketDetails;
